import {useQuery} from '@tanstack/react-query';
import {AxiosResponse} from 'axios';
import {v4 as uuid} from 'uuid';
import {
  MusicSectionListQueryParams,
  MusicTagListSearchQueryParams,
  MusicTagListDataParams,
} from '../../react_query/CommonQuery';
import axiosInstance from '../../axiosConfig';
import {IApiPaginationResponse} from '../../dto/API.dto';
import {IMusicSectionApiResponseType, ITag} from '../../dto/MediaTrack.dto';
import {
  MusicSectionUpdateValuesAPIType,
  MusicUpdateApiResponseType,
} from './MusicSection.dto';

export const queryKeyMusicSectionRequestList = () => {
  return `v1/music-section`;
};

const cleanTrackText = (text: string) =>
  text.replace(/ZZZ/gi, '').replace(/_/g, '').trim();

export const useRqGetMusicSectionList = (
  parms: MusicSectionListQueryParams = {
    per_page: 10,
    page: 1,
  },
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeyMusicSectionRequestList(), parms],
    queryFn: () => getMusicSectionList(parms),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
    staleTime: 3600000,
    select: prev => {
      const data = structuredClone(prev.data).map(
        (o: IMusicSectionApiResponseType) => ({
          ...o,
          uuid: uuid(),
          track_artist: cleanTrackText(o.track_artist),
          track_name: cleanTrackText(o.track_name),
        }),
      );
      return {...prev, data};
    },
  });
};

const getMusicSectionList = (parms: MusicSectionListQueryParams) => {
  return axiosInstance
    .get(queryKeyMusicSectionRequestList(), {
      params: parms,
    })
    .then(
      (
        response: AxiosResponse<
          IApiPaginationResponse<IMusicSectionApiResponseType[]>
        >,
      ) => {
        return response.data;
      },
    );
};
export const queryKeyMusicTagsRequestList = () => {
  return `v1/music-section/tag`;
};
export const GetTagList = (
  params: MusicTagListSearchQueryParams = {per_page: 10},
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [queryKeyMusicTagsRequestList(), params],
    queryFn: () => GetMusicSectionTagListApi(params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

export const GetMusicSectionTagListApi = (
  params: MusicTagListSearchQueryParams,
) => {
  return axiosInstance
    .get(queryKeyMusicTagsRequestList(), {
      params,
    })
    .then((response: AxiosResponse<IApiPaginationResponse<ITag[]>>) => {
      return response.data.data;
    });
};

export const uploadMusicFile = (taskId: number, data: FormData) => {
  return axiosInstance
    .post(`v1/music-section/${taskId}`, data, {
      headers: {'Content-Type': 'multipart/form-data'},
    })
    .then((response: AxiosResponse) => {
      return response.data;
    });
};

export const updateMusicApi = (
  taskId: number,
  data: MusicSectionUpdateValuesAPIType,
) => {
  return axiosInstance
    .put(`v1/music-section/${taskId}`, data)
    .then((response: AxiosResponse<MusicUpdateApiResponseType>) => {
      return response.data;
    });
};

export const GetTagListData = (
  params: MusicTagListDataParams = {per_page: 10},
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [queryKeyMusicTagsRequestList(), params],
    queryFn: () => GetMusicSectionTagListDataApi(params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

export const GetMusicSectionTagListDataApi = (
  params: MusicTagListDataParams,
) => {
  return axiosInstance
    .get(queryKeyMusicTagsRequestList(), {
      params,
    })
    .then((response: AxiosResponse<IApiPaginationResponse<ITag[]>>) => {
      return response.data;
    });
};

// List Global song request

export type IGlobalSongRequestParms = {
  page?: number;
  per_page: number;
};

type IGlobalSongRequest = {
  id: number;
  requested_at: string;
  track_name: string;
  track_artist: string;
  status: string;
};

export const queryKeyGlobalSongRequestList = () => {
  return `v1/music-section/request-song`;
};

export const globalSongRequestList = (
  params: MusicTagListSearchQueryParams,
) => {
  return axiosInstance
    .get(queryKeyGlobalSongRequestList(), {
      params,
    })
    .then(
      (
        response: AxiosResponse<IApiPaginationResponse<IGlobalSongRequest[]>>,
      ) => {
        return response.data;
      },
    );
};

export const useRqGlobalSongRequestList = (
  params: IGlobalSongRequestParms = {per_page: 10},
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [queryKeyGlobalSongRequestList(), params],
    queryFn: () => globalSongRequestList(params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};
